/**
 * ทำข้อสอบ — หนึ่งข้อต่อหนึ่งหน้าจอ
 *
 * ★ ไม่เลื่อนยาว เพราะกระดาษทดผูกกับข้อ ถ้าเลื่อนยาวเด็กจะสับสนว่าทดของข้อไหน
 *
 * ★ ตอบผิดอย่าดุ — ไม่มีคำว่า "ผิด" ไม่มีสีแดงกระพริบ ไม่มีเสียง
 *   ให้เฉลยพร้อมเหตุผล และ **แสดงรอยเขียนของเด็กข้าง ๆ เฉลย**
 *   เพื่อให้เห็นว่าตัวเองคิดพลาดตรงบรรทัดไหน — นี่คือคุณค่าจริงของกระดาษทด
 *
 * ★★ การบันทึก (จุดที่ถ้าพังเด็กจะเลิกใช้):
 *   ตอบข้อ / ปล่อยปากกา → เขียน IndexedDB **ทันที ไม่ debounce**
 *   → ยิงเซิร์ฟเวอร์แบบหน่วง 1.2 วิ → `visibilitychange` เป็น hidden ยิงทันที
 *   Safari ฆ่าแท็บเบื้องหลังโดยไม่เตือน และ `beforeunload` ไม่ยิงบน iOS
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AnswerBox } from "@/components/AnswerBox";
import { Figure } from "@/components/Figure";
import { Passage } from "@/components/Passage";
import { Center, Shell } from "@/components/Shell";
import { Scratchpad } from "@/features/pad/Scratchpad";
import { readPadOpen, writePadOpen } from "@/lib/padPref";
import { type Stroke, fitCanvas, paintAll } from "@/features/pad/strokes";
import { translate as t } from "@/i18n";
import { ApiError, api } from "@/lib/api";
import { renderMath } from "@/lib/math";
import { parentOf } from "@/lib/routes";
import {
  createFlusher,
  loadAttemptCursor,
  loadStrokesLocal,
  markAnswerSynced,
  markStrokesSynced,
  pendingAnswers,
  pendingStrokes,
  saveAnswerLocal,
  saveAttemptCursor,
  saveStrokesLocal,
  savedAnswers,
} from "@/lib/offline";
import type {
  AnswerReveal,
  AttemptOut,
  AttemptResult,
  QuizDetail,
} from "@/lib/types";

const KEYS = ["ก", "ข", "ค", "ง", "จ", "ฉ"];
const pad2 = (n: number): string => String(n).padStart(2, "0");

type SaveState = "ok" | "pending" | "offline";

export function QuizPage(): JSX.Element {
  const { quizId = "" } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [attempt, setAttempt] = useState<AttemptOut | null>(null);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<Record<string, string | null>>({});
  const [locked, setLocked] = useState<Record<string, boolean>>({});
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [strokes, setStrokes] = useState<Record<string, Stroke[]>>({});
  const [reveals, setReveals] = useState<Record<string, AnswerReveal>>({});
  const [allowFinger, setAllowFinger] = useState(false);
  // ★ กระดาษทดเริ่มที่ "ปิด" เสมอ แล้วค่อยดึงค่าที่จำไว้ของวิชานี้มาทับ (24 ก.ย. 2569)
  const [padOpen, setPadOpen] = useState(false);
  const padHydrated = useRef(false);
  const [saveState, setSaveState] = useState<SaveState>("ok");
  const [error, setError] = useState<string | null>(null);
  const [resumedNote, setResumedNote] = useState(false);
  const questionStartRef = useRef(performance.now());

  const quiz = useQuery({
    queryKey: ["quiz", quizId],
    queryFn: () => api.get<QuizDetail>(`/quizzes/${quizId}`),
  });

  const questions = useMemo(() => quiz.data?.questions ?? [], [quiz.data]);
  const current = questions[index];
  const isPractice = quiz.data?.mode === "practice";
  const subjectCode = quiz.data?.subject_code ?? null;

  // ─── เริ่ม / กลับมาทำต่อ ────────────────────────────────────
  useEffect(() => {
    if (!quizId || attempt) return;
    let cancelled = false;
    void (async () => {
      try {
        const started = await api.post<AttemptOut>("/attempts", {
          quiz_id: quizId,
          resume_if_in_progress: true,
          device: "ipad",
        });
        if (cancelled) return;
        setAttempt(started);
        if (started.resumed) setResumedNote(true);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof ApiError ? e.message : t("error.network"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt, quizId]);

  // ─── คืนสิ่งที่ทำค้างไว้: ข้อที่ค้างอยู่ + คำตอบที่เลือกไว้ ──────
  //
  // ★★ ต้องเป็น effect **แยก** ที่ขึ้นกับ `attempt` เท่านั้น
  //   เดิมโค้ดนี้อยู่ต่อท้าย `setAttempt(started)` ในเอฟเฟกต์เดียวกัน ซึ่ง **ไม่เคย
  //   ทำงานเลย**: `attempt` อยู่ใน deps ของเอฟเฟกต์นั้น พอ `setAttempt` คอมมิต
  //   React ก็ยิง cleanup ของรอบก่อนทันที ตั้ง `cancelled = true` แล้วโค้ดที่รอ
  //   `await` อยู่ก็ถูกข้ามไปเงียบ ๆ — โค้ดอ่านแล้วดูถูกต้องทุกบรรทัด
  //   อาการที่ผู้ใช้เจอคือ "ออกไปแล้วกลับมา คำตอบหายหมด" และ "ไม่ได้กลับมาข้อเดิม"
  //   (เจอตอนเขียน e2e ข้อออกกลางคันแล้วกลับมาทำต่อ — ตาเปล่าไม่มีทางเห็น)
  useEffect(() => {
    if (!attempt) return;
    let cancelled = false;
    void (async () => {
      const [cursor, saved] = await Promise.all([
        loadAttemptCursor(attempt.id),
        savedAnswers(attempt.id),
      ]);
      if (cancelled) return;
      if (cursor) setIndex(cursor.index);
      if (saved.length > 0) {
        setPicked((p) => {
          const next = { ...p };
          for (const d of saved)
            if (d.chosenChoiceId) next[d.questionId] = d.chosenChoiceId;
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // ─── ★ คืนกล่องเฉลยของข้อที่ตอบไปแล้ว (แก้บั๊ก D21 · 23 ก.ย. 2569) ─────
  //
  // อาการ: โหมดฝึก ตอบผิดไปแล้ว พอรีโหลดหน้า/กลับเข้ามาใหม่ ตัวเลือกล็อกถูกต้อง
  // แต่ **กล่องเฉลยหายไป** เด็กจึงไม่ได้เห็นวิธีคิดของข้อที่ตัวเองตอบผิด
  //
  // ต้นเหตุ: ตอนคืนสถานะจากเครื่องเราคืนแค่ `picked` — `reveals` เป็น state ในหน่วยความจำ
  // ที่หายไปพร้อมการรีโหลด และ `current.correct_choice_id` ของเด็กเป็น null เสมอ
  // ระหว่างยังทำชุดอยู่ (เฉลยไม่เคยถูกส่งมาก่อนตอบ — กันเปิด DevTools ดูล่วงหน้า)
  // `correct` จึงเป็น null → เงื่อนไขเรนเดอร์กล่องเป็นเท็จ
  //
  // ★ ขอเฉลยใหม่ปลอดภัย ไม่ใช่ช่องดูเฉลยฟรี — endpoint บังคับสองอย่างอยู่แล้ว:
  //   ชุดต้องเป็นโหมดฝึก และ **ต้องตอบข้อนั้นไปแล้ว** (attempt_service.reveal_answer)
  //
  // deps มีแค่ข้อปัจจุบัน ไม่ใส่ `chosen` — ตอนตอบสด ๆ `answer()` ยิงขอเฉลยเองอยู่แล้ว
  // ถ้าใส่ด้วยจะยิงซ้ำสองครั้งทุกครั้งที่เด็กกดตอบ
  // ─── ★ ดึงค่ากระดาษทดที่จำไว้ของวิชานี้ (24 ก.ย. 2569) ──────────────
  //
  // `subject_code` มาแบบ async พร้อม `quiz.data` จึงเป็น null ในรอบแรกเสมอ
  // ถ้าอ่านค่าตอน useState จะได้คีย์ "default" ซึ่งเป็นคนละวิชา
  //
  // `padHydrated` กันการ hydrate ซ้ำทับค่าที่เด็กเพิ่งกด — ถ้าไม่มีตัวนี้
  // การ re-render ใด ๆ ที่ทำให้ effect ยิงใหม่จะดึงค่าเก่ามาทับทันที
  useEffect(() => {
    if (padHydrated.current || !subjectCode) return;
    padHydrated.current = true;
    setPadOpen(readPadOpen(subjectCode));
  }, [subjectCode]);

  /** เปิด/ปิดกระดาษทด — จำลงเครื่องพร้อมกันเสมอ ไม่งั้นรีโหลดแล้วลืม */
  const setPad = useCallback(
    (open: boolean) => {
      setPadOpen(open);
      writePadOpen(subjectCode, open);
    },
    [subjectCode],
  );

  const revealTried = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!attempt || !current || !isPractice) return;
    const qid = current.question_id;
    if (!picked[qid] || reveals[qid] || revealTried.current.has(qid)) return;
    revealTried.current.add(qid);
    let cancelled = false;
    void (async () => {
      try {
        const revealed = await api.get<AnswerReveal>(
          `/attempts/${attempt.id}/answers/${qid}/reveal`,
        );
        if (!cancelled) setReveals((r) => ({ ...r, [qid]: revealed }));
      } catch {
        // ขอไม่สำเร็จ (เน็ตหลุด หรือคำตอบยังไม่ถึงเซิร์ฟเวอร์) — ให้ลองใหม่ได้
        // เมื่อเด็กกลับมาที่ข้อนี้อีกครั้ง ไม่ใช่วนยิงรัว ๆ อยู่ตรงนี้
        revealTried.current.delete(qid);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, current?.question_id, isPractice, picked, reveals]);

  // ─── โหลดรอยเขียนของข้อปัจจุบันจากเครื่อง ───────────────────
  useEffect(() => {
    if (!attempt || !current) return;
    let cancelled = false;
    void (async () => {
      const saved = await loadStrokesLocal(attempt.id, current.question_id);
      if (!cancelled)
        setStrokes((prev) => ({ ...prev, [current.question_id]: saved }));
    })();
    questionStartRef.current = performance.now();
    return () => {
      cancelled = true;
    };
  }, [attempt, current]);

  // ─── ตัวยิงขึ้นเซิร์ฟเวอร์ ──────────────────────────────────
  const flush = useCallback(async () => {
    if (!attempt) return;
    const answers = await pendingAnswers(attempt.id);
    const pads = await pendingStrokes(attempt.id);
    if (answers.length === 0 && pads.length === 0) {
      setSaveState("ok");
      return;
    }
    try {
      for (const draft of answers) {
        await api.put(`/attempts/${attempt.id}/answers/${draft.questionId}`, {
          chosen_choice_id: draft.chosenChoiceId,
          flagged: draft.flagged,
          time_spent_ms: draft.timeSpentMs,
        });
        await markAnswerSynced(attempt.id, draft.questionId);
      }
      for (const draft of pads) {
        // TODO(Sprint 2) — endpoint ของกระดาษทดยังไม่มี เก็บไว้ในเครื่องก่อน
        // ตารางและ D7 พร้อมแล้ว แต่ API ยังไม่ได้ทำในรอบนี้
        await markStrokesSynced(attempt.id, draft.questionId);
      }
      setSaveState("ok");
    } catch {
      // ยิงไม่สำเร็จไม่ใช่เรื่องใหญ่ — งานอยู่ในเครื่องแล้ว ลองใหม่รอบหน้า
      setSaveState(navigator.onLine ? "pending" : "offline");
    }
  }, [attempt]);

  const flusher = useMemo(() => createFlusher(() => flush()), [flush]);
  useEffect(() => () => flusher.dispose(), [flusher]);

  // ─── ตอบข้อ ─────────────────────────────────────────────────
  const answer = useCallback(
    (choiceId: string) => {
      if (!attempt || !current) return;
      // โหมดฝึก: ตอบแล้วล็อกทันที เพราะเฉลยขึ้นแล้ว แก้ได้ = โกงตัวเอง
      if (locked[current.question_id]) return;

      const spent = Math.round(performance.now() - questionStartRef.current);
      setPicked((p) => ({ ...p, [current.question_id]: choiceId }));
      if (isPractice) setLocked((l) => ({ ...l, [current.question_id]: true }));
      setSaveState("pending");

      void saveAnswerLocal({
        attemptId: attempt.id,
        questionId: current.question_id,
        chosenChoiceId: choiceId,
        flagged: Boolean(flags[current.question_id]),
        timeSpentMs: spent,
        savedAt: Date.now(),
        pending: true,
      }).then(async () => {
        if (!isPractice) {
          flusher.schedule();
          return;
        }
        // โหมดฝึกต้องเห็นเฉลยทันที จึงยิงคำตอบขึ้นไปเลยไม่รอ debounce
        // แล้วขอเฉลยของข้อนั้นกลับมา (เฉลยไม่เคยอยู่ในเครื่องก่อนตอบ — กันเปิดดูล่วงหน้า)
        try {
          await flush();
          const revealed = await api.get<AnswerReveal>(
            `/attempts/${attempt.id}/answers/${current.question_id}/reveal`,
          );
          setReveals((r) => ({ ...r, [current.question_id]: revealed }));
        } catch {
          // ขอเฉลยไม่สำเร็จ (เช่นเน็ตหลุด) — คำตอบยังอยู่ในเครื่อง
          // เด็กจะเห็นเฉลยตอนส่งชุด ไม่ใช่เสียคำตอบไป
          setSaveState(navigator.onLine ? "pending" : "offline");
        }
      });
    },
    [attempt, current, flags, flush, flusher, isPractice, locked],
  );

  const commitStrokes = useCallback(
    (next: Stroke[]) => {
      if (!attempt || !current) return;
      setStrokes((prev) => ({ ...prev, [current.question_id]: next }));
      // ★ เขียนลงเครื่องทันที ไม่ debounce
      void saveStrokesLocal({
        attemptId: attempt.id,
        questionId: current.question_id,
        strokes: next,
        savedAt: Date.now(),
        pending: true,
      }).then(() => flusher.schedule());
    },
    [attempt, current, flusher],
  );

  const goto = useCallback(
    (next: number) => {
      if (!attempt) return;
      const clamped = Math.max(0, Math.min(questions.length - 1, next));
      setIndex(clamped);
      void saveAttemptCursor({
        attemptId: attempt.id,
        quizId,
        index: clamped,
        savedAt: Date.now(),
      });
    },
    [attempt, questions.length, quizId],
  );

  const submit = useCallback(async () => {
    if (!attempt) return;
    const unanswered = questions.filter(
      (q) => picked[q.question_id] == null,
    ).length;
    // ทำไม่ครบส่งได้ แต่ต้องรู้ตัวก่อน — ไม่ใช่ห้ามส่ง
    if (
      unanswered > 0 &&
      !window.confirm(t("quiz.confirmSubmitPartial", "th", { n: unanswered }))
    )
      return;
    flusher.flushNow();
    try {
      await flush();
      const result = await api.post<AttemptResult>(
        `/attempts/${attempt.id}/submit`,
      );
      navigate(`/result/${attempt.id}`, { state: { result } });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("error.network"));
    }
  }, [attempt, flush, flusher, navigate, picked, questions]);

  if (quiz.isLoading || !attempt) {
    return (
      <Shell>
        <Center>
          <p>{t("state.loading")}</p>
        </Center>
      </Shell>
    );
  }

  if (quiz.isError || !current) {
    return (
      <Shell>
        <Center>
          <p>{error ?? t("error.network")}</p>
          <button
            type="button"
            className="quit"
            onClick={() => navigate("/learn")}
          >
            {t("nav.back")}
          </button>
        </Center>
      </Shell>
    );
  }

  const chosen = picked[current.question_id] ?? null;
  // โหมดฝึก: ตอบแล้วล็อกทันที — คำนวณจาก `picked` ด้วย เพื่อให้คำตอบที่คืนมา
  // ตอนกลับเข้ามาใหม่ยังล็อกอยู่เหมือนเดิม ไม่ใช่กลับมาแก้ได้
  const isLocked =
    Boolean(locked[current.question_id]) || (isPractice && chosen !== null);
  const revealed = reveals[current.question_id];
  // เฉลยมาจาก endpoint แยก (โหมดฝึก) หรือจากชุดที่ผู้ปกครองเปิด — ไม่เคยมาก่อนตอบ
  const correct = revealed?.correct_choice_id ?? current.correct_choice_id;
  const isRight =
    revealed?.is_correct ?? (chosen !== null && chosen === correct);
  const myStrokes = strokes[current.question_id] ?? [];

  return (
    <Shell
      lockText
      progress={(index + 1) / questions.length}
      here={quiz.data?.title}
      onQuit={() => {
        // ★ ออกกลางข้อสอบต้องยืนยันเจตนาก่อน — แต่ถามเพื่อกันกดพลาด
        //   **ไม่ใช่** เตือนว่างานจะหาย เพราะคำตอบถูกบันทึกแล้วและกลับมา
        //   ทำต่อจากข้อเดิมได้ (คำตัดสินข้อ 3) ข้อความจึงต้องพูดตามความจริง
        //   ถ้าเขียนว่า "งานจะหาย" เด็กจะกลัวจนไม่กล้าออกไปกินข้าว
        if (!window.confirm(t("quiz.confirmLeave"))) return;
        flusher.flushNow();
        navigate(parentOf(pathname) ?? "/learn");
      }}
    >
      <div className="qhead">
        <span className="qno">
          {t("quiz.position", "th", {
            current: pad2(index + 1),
            total: pad2(questions.length),
          })}
        </span>
        <span className="qtag">{current.difficulty}</span>
        <span className="spacer" />
        <span className={`saved ${saveState === "ok" ? "" : saveState}`}>
          <span className="dot" />
          {saveState === "ok"
            ? t("quiz.savedOk")
            : saveState === "pending"
              ? t("quiz.savedPending")
              : t("quiz.savedOffline")}
        </span>
      </div>

      {resumedNote ? <div className="note">{t("quiz.resumed")}</div> : null}
      {error ? <p className="err">{error}</p> : null}

      {/* ★ บทประพันธ์ที่ยกมา — บล็อกแยกบนพื้นกระดาษ (--pad) เหนือโจทย์ เด็กต้องรู้ทันที
          ว่า "นี่คือบทที่ยกมา" กับ "นี่คือคำถาม" · white-space: pre-line รักษาวรรคของกลอน
          · null = ไม่เรนเดอร์อะไรเลย ไม่ใช่กล่องเปล่า · อยู่ใน .wrap ที่ lockText ครอบอยู่แล้ว */}
      {current.passage ? <Passage text={current.passage} /> : null}
      {/* D23 — รูปประกอบโจทย์เหนือ stem · ไม่มีรูป/รูปไม่ผ่านด่าน = ไม่มี element ใน DOM */}
      <Figure svg={current.figure} />
      <p className="qtext">{renderMath(current.stem)}</p>

      <div className="choices">
        {/* ★ ทุกการเทียบใช้ `choice.id` — ตำแหน่งใช้แค่เลือกตัวอักษร ก/ข/ค/ง
            ที่โชว์เฉย ๆ ไม่มีความหมายกับการตัดสินถูก/ผิด (10-INFRA §12.12) */}
        {current.choices.map((choice, position) => {
          let cls = "ch";
          if (chosen === choice.id) cls += " picked";
          if (isLocked && correct) {
            if (choice.id === correct) cls = "ch right";
            else if (chosen === choice.id) cls = "ch chosen-wrong";
          }
          return (
            <button
              key={choice.id}
              type="button"
              className={cls}
              disabled={isLocked}
              onClick={() => answer(choice.id)}
            >
              <span className="k">{KEYS[position]}</span>
              <span>{renderMath(choice.text)}</span>
            </button>
          );
        })}
      </div>

      {/* เฉลย — โหมดฝึกขึ้นทันที · โหมดสอบไปขึ้นที่หน้าผล */}
      {isLocked && correct !== null && correct !== undefined ? (
        // D20 — กล่องเฉลยแบบใหม่ (components/AnswerBox) · ข้อความตัวเลือกที่ถูกหาจาก id ไม่ใช่ตำแหน่ง
        <AnswerBox
          correct={isRight}
          correctText={
            current.choices.find((c) => c.id === correct)?.text ?? null
          }
          explanation={revealed?.explanation ?? current.explanation}
          tip={revealed?.tip}
          keyTerms={revealed?.key_terms}
          sourceDisplay={revealed?.source_display}
          sourceBook={revealed?.source_book}
          colorKey={quiz.data?.subject_color_key}
        >
          {myStrokes.length > 0 ? (
            <div className="work">
              <h3>{t("quiz.myWork")}</h3>
              <StrokeReplay strokes={myStrokes} />
            </div>
          ) : null}
        </AnswerBox>
      ) : null}

      {/* ★ ปิด = ถอดคอมโพเนนต์ออกจากต้นไม้จริง ไม่ใช่ซ่อนด้วย CSS (24 ก.ย. 2569)
          ปลอดภัยเพราะ `onCommit` เขียนรอยเขียนลง IndexedDB ทุกครั้งที่ปล่อยปากกา
          และ `myStrokes` อยู่ใน state ของหน้านี้ ไม่ได้อยู่ในตัวกระดาษ
          ได้ผลพลอยได้คือวิชาที่ไม่ใช้กระดาษทดจะไม่ mount canvas / ไม่ผูก
          pointer handler / ไม่รัน resize เลย */}
      {padOpen ? (
        <Scratchpad
          key={current.question_id}
          value={myStrokes}
          onCommit={commitStrokes}
          allowFinger={allowFinger}
          onAllowFingerChange={setAllowFinger}
          onCollapse={() => setPad(false)}
        />
      ) : (
        <button
          type="button"
          className="padstub"
          id="padregion"
          data-testid="pad-stub"
          aria-expanded={false}
          aria-controls="padregion"
          onClick={() => setPad(true)}
        >
          <span className="lbl">{t("pad.title")}</span>
          {/* ★ ป้ายนี้สำคัญที่สุดของงานนี้ — ถ้าไม่มี เด็กที่เขียนไว้แล้วกดซ่อน
              จะคิดว่างานหายและไม่กล้ากดซ่อนอีกเลย */}
          {myStrokes.length > 0 ? (
            <span className="ink" data-testid="pad-hasink">
              <span className="dot" />
              {t("pad.hasInk")}
            </span>
          ) : null}
          <span className="spacer" />
          <span className="cta">{t("pad.open")} ▼</span>
        </button>
      )}

      {/* โหมดสอบ: ข้ามไปข้อไหนก็ได้ + ธง */}
      {!isPractice ? (
        <>
          <p className="eyebrow">{t("quiz.jumpHead")}</p>
          <div className="jump">
            {questions.map((q, i) => {
              const cls = [
                i === index ? "now" : "",
                picked[q.question_id] != null ? "done" : "",
                flags[q.question_id] ? "flag" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={q.question_id}
                  type="button"
                  className={cls}
                  onClick={() => goto(i)}
                >
                  {pad2(i + 1)}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="link"
            onClick={() =>
              setFlags((f) => ({
                ...f,
                [current.question_id]: !f[current.question_id],
              }))
            }
          >
            {flags[current.question_id] ? t("quiz.flagged") : t("quiz.flag")}
          </button>
        </>
      ) : null}

      <div className="nav">
        <button
          type="button"
          onClick={() => goto(index - 1)}
          disabled={index === 0}
        >
          {t("quiz.prev")}
        </button>
        {index === questions.length - 1 ? (
          <button
            type="button"
            className="primary"
            onClick={() => void submit()}
          >
            {t("quiz.submit")}
          </button>
        ) : (
          <button
            type="button"
            className="primary"
            onClick={() => goto(index + 1)}
          >
            {t("quiz.next")}
          </button>
        )}
      </div>
    </Shell>
  );
}

/** วาดรอยเขียนซ้ำข้าง ๆ เฉลย — ใช้ `paintAll` ตัวเดียวกับกระดาษทด */
export function StrokeReplay({ strokes }: { strokes: Stroke[] }): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = fitCanvas(canvas, 150);
    paintAll(canvas, ctx, strokes);
  }, [strokes]);
  return <canvas ref={ref} />;
}
